import React from 'react'
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons'
import { Tooltip } from 'antd'
import PropTypes from 'prop-types'
import styled from 'styled-components'

import { THEME } from '../../../../core/constants'

import { formatDate } from '../../../../common/services/formatter'

import {
  DEFINE,
  DONE,
  QC,
  QQA,
  TASK_VIEW_STATUSES,
  TASK_VIEW_TYPES,
} from '../../../constants'

const StatusCircle = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: ${({ colored, type }) => {
    if (!colored) return THEME.colors.taskViewDefault
    if (type === DEFINE) return THEME.colors.taskViewDefine
    if (type === QC) return THEME.colors.taskViewQc
    if (type === QQA) return THEME.colors.taskViewQqa
    return THEME.colors.greyBlue
  }};
  color: ${({ colored }) =>
    colored ? THEME.colors.white : THEME.colors.darkGrey};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
  transition: background 0.2s;
`

const ProgressContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`

const StepsGrid = styled.div`
  display: grid;
  grid-template-columns: auto 50px auto 50px auto;
  grid-template-rows: auto auto;
  align-items: center;
  justify-content: center;
  column-gap: 0;
`

const GridCircle = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  grid-row: 1;
`

const GridLine = styled.div`
  grid-row: 1;
  align-self: center;
  height: 3px;
  width: calc(100% + 32px);
  background: ${({ colored, type }) => {
    if (!colored) return THEME.colors.taskViewDefault
    if (type === DEFINE) return THEME.colors.taskViewDefine
    if (type === QC) return THEME.colors.taskViewQc
    if (type === QQA) return THEME.colors.taskViewQqa
    return THEME.colors.greyBlue
  }};
  border-radius: 2px;
  transition: background 0.2s;
  margin-left: -16px;
  margin-right: -16px;
`

const GridText = styled.div`
  grid-row: 2;
  text-align: center;
  font-size: 14px;
  color: ${({ colored }) =>
    colored ? THEME.colors.darkGrey : THEME.colors.darkGrey};
  font-weight: ${({ colored }) => (colored ? 600 : 400)};
  margin-top: -4px;
  min-width: 80px;
  align-self: start;
`

const TaskViewsStatus = ({ task }) => {
  const viewTypes = [DEFINE, QC, QQA]
  const { currentViewType } = task
  const currentIndex = viewTypes.indexOf(currentViewType)

  const getViewByType = type =>
    task.views.find(view => view.type === type) || { status: 'N/A', type }

  const getInfoLabels = type => {
    switch (type) {
      case DEFINE:
        return {
          by: 'Defined by',
          date: 'Defined date',
        }
      case QC:
        return {
          by: "QC'd by",
          date: "QC'd date",
        }
      case QQA:
        return {
          by: "QQA'd by",
          date: "QQA'd date",
        }
      default:
        return {}
    }
  }

  const getResolvedInfo = view => {
    if (view.status === DONE) {
      const infoLabels = getInfoLabels(view.type)
      const resolvedBy = view.resolvedBy?.name || 'System'

      return (
        <span>
          {infoLabels.by}: {resolvedBy}
          <br />
          {infoLabels.date}: {formatDate(view.resolvedDate, true)}
        </span>
      )
    }
    return ''
  }

  const getIcon = (view, idx) => {
    if (view.status === DONE) return <CheckCircleOutlined />
    if (idx === currentIndex) return <LoadingOutlined />
    if (idx > currentIndex) return <ClockCircleOutlined />
    return null
  }

  return (
    <ProgressContainer>
      <StepsGrid>
        <GridCircle style={{ gridColumn: 1 }}>
          <Tooltip
            title={getResolvedInfo(getViewByType(viewTypes[0]))}
            trigger="hover"
            placement="top"
            overlayClassName="task-view-status-tooltip"
          >
            <StatusCircle colored={currentIndex >= 0} type={viewTypes[0]}>
              {getIcon(getViewByType(viewTypes[0]), 0)}
            </StatusCircle>
          </Tooltip>
        </GridCircle>
        <GridLine
          style={{ gridColumn: 2 }}
          colored={currentIndex >= 0 && currentIndex >= 1}
          type={viewTypes[0]}
        />
        <GridCircle style={{ gridColumn: 3 }}>
          <Tooltip
            title={getResolvedInfo(getViewByType(viewTypes[1]))}
            trigger="hover"
            placement="top"
            overlayClassName="task-view-status-tooltip"
          >
            <StatusCircle colored={currentIndex >= 1} type={viewTypes[1]}>
              {getIcon(getViewByType(viewTypes[1]), 1)}
            </StatusCircle>
          </Tooltip>
        </GridCircle>
        <GridLine
          style={{ gridColumn: 4 }}
          colored={currentIndex >= 1 && currentIndex >= 2}
          type={viewTypes[1]}
        />
        <GridCircle style={{ gridColumn: 5 }}>
          <Tooltip
            title={getResolvedInfo(getViewByType(viewTypes[2]))}
            trigger="hover"
            placement="top"
            overlayClassName="task-view-status-tooltip"
          >
            <StatusCircle colored={currentIndex >= 2} type={viewTypes[2]}>
              {getIcon(getViewByType(viewTypes[2]), 2)}
            </StatusCircle>
          </Tooltip>
        </GridCircle>
        <GridText colored={currentIndex >= 0} style={{ gridColumn: 1 }}>
          {TASK_VIEW_TYPES[viewTypes[0]].name} -{' '}
          {TASK_VIEW_STATUSES[getViewByType(viewTypes[0]).status]?.name ||
            getViewByType(viewTypes[0]).status}
        </GridText>
        <div style={{ gridColumn: 2 }} />
        <GridText colored={currentIndex >= 1} style={{ gridColumn: 3 }}>
          {TASK_VIEW_TYPES[viewTypes[1]].name} -{' '}
          {TASK_VIEW_STATUSES[getViewByType(viewTypes[1]).status]?.name ||
            getViewByType(viewTypes[1]).status}
        </GridText>
        <div style={{ gridColumn: 4 }} />
        <GridText colored={currentIndex >= 2} style={{ gridColumn: 5 }}>
          {TASK_VIEW_TYPES[viewTypes[2]].name} -{' '}
          {TASK_VIEW_STATUSES[getViewByType(viewTypes[2]).status]?.name ||
            getViewByType(viewTypes[2]).status}
        </GridText>
      </StepsGrid>
    </ProgressContainer>
  )
}

TaskViewsStatus.propTypes = {
  task: PropTypes.shape({
    currentViewType: PropTypes.string.isRequired,
    views: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.string.isRequired,
        status: PropTypes.string.isRequired,
        resolvedBy: PropTypes.shape({
          name: PropTypes.string,
        }),
        resolvedDate: PropTypes.string,
      }),
    ).isRequired,
  }).isRequired,
}

export default TaskViewsStatus
